/**
 * @description
 * Stripe invoice service for managing invoices and billing.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { StripeInvoice } from "./types";

/**
 * Stripe invoice service
 */
export class StripeInvoiceService extends BaseAPIClient {
  /**
   * Get invoices with optional filters
   */
  async getInvoices(filters?: {
    limit?: number;
    clientId?: string;
    status?: string;
    invoiceType?: 'usage' | 'subscription';
  }): Promise<StripeInvoice[]> {
    const params = new URLSearchParams();
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.clientId) params.append('clientId', filters.clientId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.invoiceType) params.append('invoiceType', filters.invoiceType);
    
    const queryString = params.toString();
    const url = queryString ? `/stripe/invoices?${queryString}` : '/stripe/invoices';
    
    const response = await this.request<{ invoices: StripeInvoice[] }>(url);
    return response.invoices;
  }

  /**
   * Bulk update invoices
   */
  async bulkUpdateInvoices(invoiceIds: string[], updates: { status?: string }): Promise<void> {
    await this.request('/stripe/invoices/bulk', {
      method: 'PUT',
      body: JSON.stringify({ invoiceIds, updates }),
    });
  }

  /**
   * Create manual invoice
   */
  async createManualInvoice(clientId: string, items: Array<{ description: string; amount: number; quantity: number }>): Promise<any> {
    const response = await this.request<{ invoice: any }>('/stripe/invoices/manual', {
      method: 'POST',
      body: JSON.stringify({ clientId, items }),
    });
    return response.invoice;
  }

  /**
   * Create subscription invoice
   */
  async createSubscriptionInvoice(clientId: string, subscriptionId: string): Promise<any> {
    const response = await this.request<{ invoice: any }>('/stripe/invoices/subscription', {
      method: 'POST',
      body: JSON.stringify({ clientId, subscriptionId }),
    });
    return response.invoice;
  }

  /**
   * Generate usage invoice manually
   */
  async generateUsageInvoice(clientId: string): Promise<{ invoiceId: string; message: string }> {
    return this.request<{ invoiceId: string; message: string }>(`/stripe/invoices/generate-usage/${clientId}`, {
      method: 'POST',
    });
  }
}

// Export singleton instance
export const stripeInvoiceService = new StripeInvoiceService();
